import { useEffect, useState } from 'react'

import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled, css } from 'styled-components'

import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { useManifestTableTracking } from '../../../hooks/useManifestTableTracking'
import { useManifestContext } from '../ManifestContext'

const Body = styled.div`
  align-items: center;
  background-color: white;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.375rem;
  display: flex;
  flex: 1;
  min-width: 16rem;

  &:focus-within {
    border-color: ${Colors.Semantic.BStock.default.lightBackground};
  }
`

const searchButtonBase = css`
  background: none;
  border: none;
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;
  padding: 0.5rem;

  &:hover {
    background-color: ${Colors.Semantic.Neutral.light_grey};
    color: ${Colors.Semantic.Neutral.black};
  }
`

const SubmitButton = styled.button`
  ${searchButtonBase}
  border-radius: 0.375rem 0 0 0.375rem;
  font-size: 0.75rem;
  padding-left: 0.75rem;
`

const ClearButton = styled.button`
  ${searchButtonBase}
  border-radius: 0 0.375rem 0.375rem 0;
  font-size: 0.875rem;
`

const Input = styled.input`
  ${Typography.Body4}
  background: transparent;
  border: none;
  color: ${Colors.Semantic.Neutral.black};
  outline: none;
  width: 100%;

  &::placeholder {
    color: ${Colors.Semantic.Neutral.dark_grey};
  }
`

export const SearchInput = () => {
  const { formatMessage } = useIntl()
  const { listing, search, setSearch } = useManifestContext()
  const { onManifestSearch, onManifestSearchClear } =
    useManifestTableTracking(listing)

  const [inputValue, setInputValue] = useState(search)
  useEffect(() => setInputValue(search), [search]) // oxlint-disable-line react/set-state-in-effect

  const submitSearch = (value: string) => {
    const trimmed = value.trim()
    setSearch(trimmed)
    if (trimmed) {
      onManifestSearch(trimmed)
    } else {
      onManifestSearchClear()
    }
  }

  const clearSearch = () => {
    setInputValue('')
    submitSearch('')
  }

  return (
    <Body>
      <SubmitButton
        aria-label={formatMessage({
          id: 'Listing.manifestAgGrid.searchBar.submit',
        })}
        onClick={() => submitSearch(inputValue)}
        type="button"
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </SubmitButton>
      <Input
        aria-label={formatMessage({
          id: 'Listing.manifestAgGrid.searchBar.searchPlaceholder',
        })}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submitSearch(inputValue)
        }}
        placeholder={formatMessage({
          id: 'Listing.manifestAgGrid.searchBar.searchPlaceholder',
        })}
        value={inputValue}
      />
      {inputValue && (
        <ClearButton
          aria-label={formatMessage({
            id: 'Listing.manifestAgGrid.searchBar.clearSearch',
          })}
          onClick={clearSearch}
          type="button"
        >
          <FontAwesomeIcon icon={faXmark} />
        </ClearButton>
      )}
    </Body>
  )
}
